import axios from "axios";
import { getToken } from "./authApi";
import { ApiResponse, BaseCreateResponse, BaseInfo, BaseResponse, BaseResponseWithData, baseUrl, deleteWithBodyAndAuth, deleteWithAuth, getWithAuth, Guid, ListRespone, patchWithAuth, postWithAuth, postWithAuthWitoutParams } from "./baseApi"

export type MosaicCreateRequest = {
    name: string,
    columnsCount:number,
    rowsCount: number,
}

export type MosaicModel = {
    id: Guid,
    name: string,
    columnsCount:number,
    rowsCount: number,
    players: string[],
    createdAt: string
}

export type MosaicModelSingle = {
    id: Guid,
    name: string,
    columnsCount:number,
    rowsCount: number,
    elements:MosaicElement[],
}

export enum MosaicElementType {
    Main = 0,
    Image = 1,
    Clock = 2,
    MediaLoop = 3,
    Youtube = 4,
    WebView = 5,
    WebViewLocal = 6,
    Rss = 7,
    Weather = 8,
    WeatherMultiCity = 9,
    GifControl = 10
}

export enum WidgetLayoutOrientation {
    Horizontal = 0,
    Vertical = 1
}

export interface IMediaLoopItem {
    id:string,
    name?: string,
    duration?: number
}

export interface MosaicElement {
    id: Guid,
    startColumn: number,
    startRow: number,
    width: number,
    height: number,
    type: MosaicElementType,
    staticImageName?: string,
    staticImageUrl?: string,
    recourceName: string,
    clockBackgroundColour?: string,
    clockTextColour?: string,
    clockTextFormat?: string,
    mediaLoopMedias?: IMediaLoopItem[],
    url?: string,
    name?: string,
    mute?: boolean,
    youtubeIsLive?: boolean,
    parameters?: RssParameters | WeatherParameters | WeatherMultiCityParameters | GifControlParameters
}

export interface RssParameters {
    type: "rss",
    url?: string,
    numberOfLines?: number,
    maxItems?: number,
    backgroundImageRecourceBlobName?: string,
    backgroundImageUrl?: string,
    fallbackImageRecourceBlobName?: string,
    fallbackImageUrl?: string,
    colourOfHeader?: string,
    colourOfText?: string,
    // Header Typography
    headerFont?: number,
    headerFontSize?: number,
    headerFontStyle?: number,
    headerAlignment?: number,
    // Text Typography
    textFont?: number,
    textFontSize?: number,
    textFontStyle?: number,
    textAlignment?: number,
    hideText?: boolean,
    // Margins and Spacing
    marginTop?: number,
    marginRight?: number,
    marginLeft?: number,
    spacingBetweenTextAndHeader?: number,
    spacingBetweenItems?: number,
    // Update and Rotation Settings
    updateIntervalMinutes?: number,
    rotationIntervalSeconds?: number,
    rotateInRandom?: boolean,
    loadLastXHours?: number
}

export enum WeatherForecastType {
    Hourly = 0,
    Daily = 1
}

export interface WeatherParameters {
    type: "weather",
    city: string,
    // Section visibility
    showCurrentWeather?: boolean,
    showForecast?: boolean,
    forecastType?: WeatherForecastType,
    itemCount?: number,
    updateIntervalMinutes?: number,
    // Background
    noBackground?: boolean,
    backgroundImageRecourceBlobName?: string,
    backgroundImageUrl?: string,
    // Colors
    textColour?: string,
    backgroundColour?: string,
    accentColour?: string,
    // Font sizes
    cityNameFontSize?: number,
    weatherDescriptionFontSize?: number,
    currentTemperatureFontSize?: number,
    windFontSize?: number,
    aqiFontSize?: number,
    forecastTimeFontSize?: number,
    forecastTempFontSize?: number,
    forecastIconSize?: number,
    currentIconSize?: number,
    // Padding & Spacing
    leftPadding?: number,
    spaceBetweenCurrentWeatherAndAirQuality?: number,
    spaceBetweenAirQualityAndForecast?: number,
    // AQI visibility
    showAqi?: boolean,
    // Pollen visibility
    showGrassPollen?: boolean,
    showBirchPollen?: boolean,
    showRagweedPollen?: boolean,
    showAlderPollen?: boolean,
    showMugwortPollen?: boolean,
    showOlivePollen?: boolean
}

export interface WeatherMultiCityParameters {
    type: "weatherMultiCity",
    cities: string[],
    updateIntervalMinutes?: number,
    orientation?: WidgetLayoutOrientation,
    noBackground?: boolean,
    textColour?: string,
    backgroundColour?: string,
    backgroundImageRecourceBlobName?: string,
    backgroundImageUrl?: string,
    // Font sizes
    cityNameFontSize?: number,
    weatherDescriptionFontSize?: number,
    currentTemperatureFontSize?: number,
    windFontSize?: number,
    currentIconSize?: number,
    // Layout spacing
    leftPadding?: number,
    spaceBetweenCities?: number,
}

export interface MosaicElementAdd extends Omit<MosaicElement, 'id'> {
    id: Guid | null;
}

export interface GifControlParameters {
    type: "gifControl",
    gifName: string,
    gifImageUrl?: string,
}

export interface MosaicElementsArrayAddRequest {
    elements: MosaicElementAdd[]
}

export interface MosaicElementsArrayDeleteRequest {
    ids: Guid[]
}

export type UploadMosaicImage = {
    id:Guid,
    name:string,
    url:string,
    originalName: string
}

export interface MosaicTimeslotRequest {
    name: string,
    start: string,
    end: string
}

export interface MosaicTimeslotResponse extends MosaicTimeslotRequest {
    id: Guid
}

export type MosaicDuplicateRequest = {
    name: string
}

export const getMosaicSingle = async(id: Guid) : Promise<ApiResponse<BaseResponseWithData<MosaicModelSingle>>> => {
    return await getWithAuth({url : `${baseUrl}mosaic/${id}`});
}

export const getMosaicList = async() : Promise<ApiResponse<ListRespone<MosaicModel>>> => {
    return await getWithAuth<ListRespone<MosaicModel>>({url : `${baseUrl}mosaic`});
}

export const postMosaicTemplate = async(request:MosaicCreateRequest) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<MosaicCreateRequest,BaseCreateResponse>(`${baseUrl}mosaic`, request);
}

export const postMosaicNewElements = async(request:MosaicElementsArrayAddRequest, id:Guid) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<MosaicElementsArrayAddRequest,BaseCreateResponse>(`${baseUrl}mosaic/${id}/elements`, request);
}

export const deleteMosaicElements = async ( id: Guid, mosaicIds: MosaicElementsArrayDeleteRequest) :Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithBodyAndAuth<MosaicElementsArrayDeleteRequest, BaseResponse>(`${baseUrl}mosaic/${id}/elements`, mosaicIds);
}

export const deleteMosaic = async (id: Guid) :Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseResponse>(`${baseUrl}mosaic`, id);
}

export const uploadImage = async (formData: any) : Promise<{data:{data: UploadMosaicImage}, status: number}> => {
    return await axios.post(`${baseUrl}recource`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization' : getToken()
        }, 
    });
}

export const getMosaicTimeslots = async (id: Guid): Promise<ApiResponse<ListRespone<MosaicTimeslotResponse>>> => {
    return await getWithAuth<ListRespone<MosaicTimeslotResponse>>({url : `${baseUrl}mosaic/${id}/timeslots`});
}

export const postMosaicTimeslot = async (request: MosaicTimeslotRequest, id:Guid): Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<MosaicTimeslotRequest,BaseCreateResponse>(`${baseUrl}mosaic/${id}/timeslots`, request);
}

export const deleteMosaicTimeslot = async (mosaicId:Guid, slotId:Guid):Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseResponse>(`${baseUrl}mosaic/${mosaicId}/timeslots`, slotId);
}

export const duplicateMosaic = async (id: Guid, request: MosaicDuplicateRequest): Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<MosaicDuplicateRequest, BaseCreateResponse>(`${baseUrl}mosaic/${id}/duplicate`, request);
}